import { useState, type FunctionComponent } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import reduce from 'lodash/reduce';
import toLower from 'lodash/toLower';
import { COUNTRY_SELECTION, startCase } from '@fff-web/fff-utilities';
import AddressOption from '../AddressOption';
import Button from '../Button';
import CloseButton from '../CloseButton';
import Modal from '../Modal';
import * as styles from './AddressVerificationModal.module.scss';

type AddressData = {
  primaryLine: string;
  secondaryLine?: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string;
};

type AddressVerificationModalProps = {
  modal: 'suggested' | 'undeliverable' | null;
  data: {
    submitted: AddressData;
    verified: AddressData;
  };
  loading: boolean;
  payload?: unknown;
  closeModal: () => void;
  onClickUseThisAddress: (selected: boolean, data: unknown, resetState: () => void) => void;
  onClickUseAsEntered: (payload: unknown) => void;
};

const MODAL_TYPES = {
  SUGGESTED: 'suggested',
  UNDELIVERABLE: 'undeliverable',
};

const getSuggestedFields = (submitted: AddressData, verified: AddressData) =>
  reduce(
    verified,
    (result, value, key) => {
      return isEqual(value, submitted[key as keyof AddressData]) ? result : [...result, key];
    },
    [] as string[],
  );

const formatData = ({
  primaryLine,
  secondaryLine,
  city,
  state,
  zip,
  countryCode,
}: AddressData) => ({
  addressLine1: primaryLine,
  addressLine2: secondaryLine,
  city,
  state,
  zipCode: zip,
  country: countryCode,
});

const formatAddress = (value: string) => startCase(toLower(value));

const AddressVerificationModal: FunctionComponent<AddressVerificationModalProps> = ({
  modal,
  data,
  loading,
  closeModal,
  onClickUseThisAddress,
  onClickUseAsEntered,
  payload,
}) => {
  const { submitted, verified } = data;
  const [suggestedSelected, setSuggestedSelected] = useState(true);
  const toggleSelected = () => setSuggestedSelected((prevState) => !prevState);
  const suggestedFields = getSuggestedFields(submitted, verified);

  const resetState = () => setSuggestedSelected(true);
  const handleClickClose = () => {
    resetState();
    closeModal();
  };
  const handleClickUseThisAddress = () => {
    if (suggestedSelected) {
      const suggestedData = formatData(verified);
      onClickUseThisAddress(suggestedSelected, suggestedData, resetState);
    } else {
      onClickUseThisAddress(suggestedSelected, {}, resetState);
    }
  };

  return (
    <Modal isOpen={!!modal} onRequestClose={handleClickClose} className={styles.modal}>
      <div className={styles.container}>
        <div className={styles.closeButton}>
          <CloseButton onClick={handleClickClose} />
        </div>
        <div className={styles.content}>
          {modal === MODAL_TYPES.SUGGESTED && (
            <>
              <h1 className={styles.title}>
                Oops! <span>Please Verify</span>
              </h1>
              <div className={styles.wrapper}>
                <p className={styles.subtitle}>
                  We were not able to verify your shipping address, please choose one of the options
                  below.
                </p>
                <p className={`${styles.highlighted} ${styles.suggestion}`}>
                  We have highlighted our suggestions in green.
                </p>
              </div>
              <div className={styles.options}>
                <AddressOption
                  title="Suggested address"
                  data={verified}
                  suggestedFields={suggestedFields}
                  isSelected={suggestedSelected}
                  onClick={toggleSelected}
                />
                <AddressOption
                  title="Address as entered"
                  data={submitted}
                  isSelected={!suggestedSelected}
                  onClick={toggleSelected}
                />
              </div>
              <Button
                onClick={handleClickUseThisAddress}
                className={styles.button}
                disabled={loading}
              >
                Use this Address
              </Button>
            </>
          )}
          {modal === MODAL_TYPES.UNDELIVERABLE && (
            <>
              <h1 className={styles.title}>
                Oops! <span>Please Verify</span>
              </h1>
              <p className={styles.subtitle}>
                The address you entered is not deliverable.
                <span>Please edit your address below.</span>
              </p>
              <div className={styles.address}>
                <p>{formatAddress(submitted.primaryLine)}</p>
                {submitted.secondaryLine && <p>{formatAddress(submitted.secondaryLine)}</p>}
                <p>
                  {`${formatAddress(submitted.city)}, ${submitted.state || ''} ${submitted.zip}`}
                </p>
                <p>{get(find(COUNTRY_SELECTION, ['code', submitted.countryCode]), 'name')}</p>
              </div>
              <Button onClick={handleClickClose} className={styles.button}>
                Edit Address
              </Button>
              <button
                type="button"
                className={styles.link}
                onClick={() => onClickUseAsEntered(payload)}
                disabled={loading}
              >
                Use address as entered
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddressVerificationModal;
